import './index.css';
import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useApiQuery } from '../../hooks.js';
import { Button, Loading, PageIntro, RequestError, SelectField, SourceLink, TextField } from '../../components/ui.js';
import { PartyLogo } from '../../components/party-logo.js';
import { PageHero } from '../../components/page-hero.js';
import { number } from '../../format.js';
import { PoliticiansPage } from '../politicians/index.js';
import { applyClientSeo, createPageSeo } from '../../seo.js';

interface Party { abbreviation: string; name: string; source_url: string; count: number; chamber_count: number; senate_count: number }

type PartyScope = 'all' | 'chamber' | 'senate' | 'represented' | 'unrepresented';
type PartySort = '' | 'representatives' | 'name' | 'abbreviation';

const partyScopes: { value: PartyScope; label: string }[] = [
  { value: 'all', label: 'Todas as siglas' },
  { value: 'chamber', label: 'Câmara dos Deputados' },
  { value: 'senate', label: 'Senado Federal' },
  { value: 'represented', label: 'Atuais com representantes' },
  { value: 'unrepresented', label: 'Sem representantes' },
];

function normalizeSearch(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR').trim();
}

function matchesScope(party: Party, scope: PartyScope) {
  if (scope === 'chamber') return party.chamber_count > 0;
  if (scope === 'senate') return party.senate_count > 0;
  if (scope === 'represented') return party.count > 0;
  if (scope === 'unrepresented') return party.count === 0;
  return true;
}

export function PartiesPage() {
  const result = useApiQuery<Party[]>(['parties'], '/api/parties');
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<PartyScope>('all');
  const [sort, setSort] = useState<PartySort>('');
  const intro = <PageHero eyebrow="ORGANIZAÇÃO PARTIDÁRIA" title="Partidos e siglas" description="Consulte partidos e siglas históricas. Filtre por Casa legislativa e presença de representantes." illustration="skyline" />;
  const parties = useMemo(() => {
    const query = normalizeSearch(search);
    return (result.data ?? [])
      .filter((party) => matchesScope(party, scope))
      .filter((party) => !query || normalizeSearch(`${party.abbreviation} ${party.name}`).includes(query))
      .sort((left, right) => {
        if (sort === 'name') return left.name.localeCompare(right.name, 'pt-BR') || left.abbreviation.localeCompare(right.abbreviation, 'pt-BR');
        if (sort === 'abbreviation') return left.abbreviation.localeCompare(right.abbreviation, 'pt-BR');
        return right.count - left.count || left.abbreviation.localeCompare(right.abbreviation, 'pt-BR');
      });
  }, [result.data, search, scope, sort]);
  if (result.isLoading) return <>{intro}<Loading /></>;
  if (result.error) return <>{intro}<RequestError message={result.error.message} /></>;
  return <>{intro}
    <section className="party-browser" aria-label="Buscar e filtrar partidos">
      <div className="party-search-field"><Search aria-hidden="true" /><TextField label="" aria-label="Buscar partido (sigla ou nome)" type="search" value={search} onValueChange={setSearch} placeholder="Buscar partido (sigla ou nome)" /></div>
      <div className="party-browser-options">
        <div className="party-scope-filters" role="group" aria-label="Filtrar partidos">
          {partyScopes.map((item) => <Button key={item.value} variant="unstyled" className={`party-scope-button${scope === item.value ? ' is-active' : ''}`} aria-pressed={scope === item.value} onClick={() => setScope(item.value)}>{item.label}</Button>)}
        </div>
        <SelectField label="" aria-label="Ordenar por" labelClassName="party-sort-field" value={sort} onValueChange={(value) => setSort(value as PartySort)}>
          <option value="" disabled>Ordenar por</option>
          <option value="representatives">Número de representantes</option>
          <option value="name">Nome do partido</option>
          <option value="abbreviation">Sigla</option>
        </SelectField>
      </div>
      <p className="party-results-count" aria-live="polite"><strong>{number(parties.length)}</strong> {parties.length === 1 ? 'partido encontrado' : 'partidos encontrados'}</p>
    </section>
    {parties.length ? <div className="party-grid">{parties.map((party) => <Link key={party.abbreviation} className="party-card panel" to={`/partidos/${encodeURIComponent(party.abbreviation)}`}>
      <PartyLogo abbreviation={party.abbreviation} decorative />
      <div className="party-card-identity"><h2>{party.abbreviation}</h2><p>{party.name}</p></div>
      <div className="party-card-count"><strong>{number(party.count)}</strong> representantes<small>{number(party.chamber_count)} deputados federais · {number(party.senate_count)} senadores</small></div>
      <span className="party-card-link">Ver partido →</span>
    </Link>)}</div> : <div className="party-empty-state" role="status"><strong>Nenhum partido encontrado</strong><p>Ajuste a busca ou selecione outros filtros.</p></div>}
  </>;
}

export function PartyDetailPage() {
  const { party = '' } = useParams();
  const decoded = decodeURIComponent(party);
  const result = useApiQuery<Party[]>(['parties'], '/api/parties');
  const detail = result.data?.find((item) => item.abbreviation.toLowerCase() === decoded.toLowerCase());
  useEffect(() => {
    if (!detail) return;
    applyClientSeo(createPageSeo(`/partidos/${encodeURIComponent(detail.abbreviation)}`, '', {
      type: 'party', name: `${detail.abbreviation} · ${detail.name}`,
      description: `${number(detail.count)} perfis de deputados federais e senadores atualmente registrados.`, sourceUrl: detail.source_url,
    }));
  }, [detail?.abbreviation, detail?.name, detail?.count]);
  if (result.isLoading) return <Loading />;
  if (result.error) return <RequestError message={result.error.message} />;
  if (!detail) return <RequestError message={`Não encontramos o partido ${decoded}.`} />;
  return <><PageIntro eyebrow="PARTIDOS POLÍTICOS" title={`${detail.abbreviation} · ${detail.name}`} description={`${number(detail.count)} perfis de deputados federais e senadores atualmente registrados.`}>{detail.source_url && <SourceLink href={detail.source_url}>Fonte oficial do partido</SourceLink>}</PageIntro><PoliticiansPage initialParty={detail.abbreviation} showIntro={false} /></>;
}

